import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={className}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yVideo = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" data-testid="hero-section" ref={ref} aria-label="Introduction" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: yVideo }} className="absolute inset-0 scale-[1.06]">
        <video
          className="hero-video"
          data-testid="hero-background-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/blueprint-art.png"
          aria-hidden="true"
        >
          <source src="/assets/blueprint-video.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <div className="hero-vignette absolute inset-0" aria-hidden="true" />
      <div className="blueprint-grid absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="crosshair left-5 top-24" aria-hidden="true" />
      <div className="crosshair bottom-24 right-6" aria-hidden="true" />

      <motion.div style={{ y: yText, opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-36 sm:px-6 lg:px-8 lg:pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.9 }}
          data-testid="hero-sheet-label"
          className="mb-7 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.35em] text-gold/90 sm:text-[11px]"
        >
          <span className="inline-block h-px w-10 bg-gold/70" />
          Sheet VHE-01 — Site Plan · Albuquerque, NM
        </motion.p>

        <h1 className="max-w-4xl font-serif text-4xl font-bold leading-[1.22] tracking-tight text-parchment sm:text-5xl sm:leading-[1.24] lg:text-[4.6rem]" data-testid="hero-headline">
          <Line delay={0.35}>Architecting Innovation.</Line>
          <Line delay={0.5} className="ember-text italic">Empowering Vision.</Line>
          <Line delay={0.65}>Securing Legacy.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.9, ease: EASE }}
          data-testid="hero-supporting-copy"
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          Vision-Heirs Enterprises builds the structures that turn vision into sustainable innovation, meaningful impact, and enduring legacy.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#blueprint"
            data-testid="hero-cta-explore-impact"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-goldlight via-gold to-goldlight bg-[length:200%_100%] bg-left px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-navy shadow-[0_10px_36px_-10px_rgba(212,175,55,0.55)] transition-[background-position,transform] duration-500 hover:bg-right hover:-translate-y-0.5"
          >
            Explore Our Impact
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-schedule-consultation"
            className="group inline-flex items-center gap-3 rounded-full border border-gold/70 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-400 hover:bg-gold/10"
          >
            Schedule a Consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </motion.div>

      <motion.aside
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        aria-hidden="true"
        className="absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 flex-col items-end gap-3 lg:flex"
      >
        {["Vision", "Strategy", "Structure", "Impact", "Legacy"].map((w, i) => (
          <motion.span
            key={w}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5 + i * 0.12, duration: 0.7, ease: EASE }}
            className={`font-mono text-[11px] uppercase tracking-[0.35em] ${i === 4 ? "text-gold" : "text-slate-400"}`}
          >
            {w}
          </motion.span>
        ))}
      </motion.aside>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-slate-400">Scroll</span>
        <span className="relative h-10 w-px overflow-hidden bg-slate-600/60">
          <span className="cue-dot absolute left-0 top-0 h-3 w-px bg-gold" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
